import "./UploadDealDocumentModal.css";

import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { ServiceError } from "../../../../../service/Shared";
import {
  ActionButtons,
  Column,
  ErrorBox,
  LayeredSelect,
  Modal,
  MultipleFileUpload,
  RadioButtonGroup,
  Row,
  SingleFileUpload,
  TextInput,
} from "../../../../../widget";
import { RadioChangeEvent } from "../../../../../widget/forms/Input/RadioButtonGroup/components";
import { ADD_NEW_DOCUMENT_TYPE_OPTION, useUploadDealDocumentModal } from "./useUploadDealDocumentModal";

interface UploadDealDocumentModalProps {
  show: boolean;
  closeModal: () => void;
  dealUuid: string;
  dealDocumentUuid?: string;
  customVariant?: string;
  documentType?: string;
  refreshDocuments?: boolean;
  setRefreshDocuments?: Dispatch<SetStateAction<boolean>>;
}

export const UploadDealDocumentModal = ({
  show,
  closeModal,
  dealUuid,
  dealDocumentUuid,
  customVariant,
  documentType,
  refreshDocuments,
  setRefreshDocuments,
}: UploadDealDocumentModalProps): JSX.Element => {
  const [isAddingNewDocumentType, setIsAddingNewDocumentType] = useState(false);
  const {
    fileUploadRef,
    customVariant: customVariantValue,
    documentType: documentTypeValue,
    documentTypes,
    errors,
    isMultipleUpload,
    isHandleSubmitLoading,
    setCustomVariant,
    setDocumentType,
    setFiles,
    setIsMultipleUpload,
    clearFileInput,
    handleCancel,
    handleSubmit,
  } = useUploadDealDocumentModal({
    closeModal,
    dealUuid,
    dealDocumentUuid,
    customVariant,
    documentType,
    refreshDocuments,
    setRefreshDocuments,
    show,
  });

  useEffect(() => {
    if (!show) {
      setIsAddingNewDocumentType(false);
    }
  }, [show]);

  const isCustomVariantReadonly = customVariant !== undefined;
  const isDocumentTypeReadonly = documentType !== undefined;
  const shouldHideUploadType = isCustomVariantReadonly && isDocumentTypeReadonly;
  const hasDocumentTypeOptions =
    !isAddingNewDocumentType && documentTypes !== undefined && documentTypes.some((group) => group.options.length > 0);

  useEffect(() => {
    if (shouldHideUploadType && isMultipleUpload) {
      clearFileInput();
      setFiles(undefined);
      setIsMultipleUpload(false);
    }
  }, [clearFileInput, isMultipleUpload, setFiles, setIsMultipleUpload, shouldHideUploadType]);

  const renderDocumentTypeInput = (): JSX.Element => {
    if (isDocumentTypeReadonly) {
      return <TextInput name="Document type" label="Document type" value={documentTypeValue} disabled />;
    }

    if (hasDocumentTypeOptions) {
      return (
        <div className="ModalLayeredDropdown">
          <LayeredSelect
            data={documentTypes || []}
            label="Document type"
            onChange={(value) => {
              if (value === ADD_NEW_DOCUMENT_TYPE_OPTION) {
                setIsAddingNewDocumentType(true);
                setDocumentType(undefined);
                return;
              }
              setIsAddingNewDocumentType(false);
              setDocumentType(value);
            }}
            placeholder="Select document type"
          />
        </div>
      );
    }

    return (
      <TextInput
        name="Document type"
        label="Document type"
        value={documentTypeValue}
        onChange={(value) => {
          setIsAddingNewDocumentType(true);
          setDocumentType(value);
        }}
      />
    );
  };

  return (
    <div className="UploadDealDocumentModal">
      <Modal show={show} title="Upload deal document" onClose={handleCancel}>
        {errors && errors.length > 0 && (
          <Row spacingV="ml" justify="center">
            <Column span={6}>
              <ErrorBox
                title="Please solve the below issues before saving"
                text={errors.map((error: ServiceError) => error.message)}
              />
            </Column>
          </Row>
        )}
        <form onSubmit={(e) => handleSubmit(e)}>
          <Row spacingV="l" justify="center">
            <Column span={6} hasNoPadding>
              {renderDocumentTypeInput()}
            </Column>
          </Row>
          {!isMultipleUpload && (
            <Row spacingV="l" justify="center">
              <Column span={6} hasNoPadding>
                <TextInput
                  name="Custom variant"
                  label="Custom variant"
                  value={customVariantValue}
                  disabled={isCustomVariantReadonly}
                  onChange={(value) => setCustomVariant(value)}
                />
              </Column>
            </Row>
          )}
          {!shouldHideUploadType && (
            <Row spacingV="l" justify="center">
              <Column span={6} hasNoPadding>
                <div className="ModalRadioButtons">
                  <RadioButtonGroup
                    label="Upload type"
                    data={[
                      { key: false, value: "Single" },
                      { key: true, value: "Multiple files" },
                    ]}
                    direction="vertical"
                    value={isMultipleUpload}
                    onChange={(e: RadioChangeEvent) => {
                      const nextIsMultiple = e.target.value as boolean;
                      clearFileInput();
                      setFiles(undefined);
                      if (nextIsMultiple) {
                        setCustomVariant(undefined);
                      }
                      setIsMultipleUpload(nextIsMultiple);
                    }}
                  />
                </div>
              </Column>
            </Row>
          )}
          <Row spacingV="l" justify="center">
            {isMultipleUpload ? (
              <Column span={6} hasNoPadding>
                <MultipleFileUpload
                  ref={fileUploadRef}
                  title="Upload documents"
                  accept="*"
                  onChange={(e) => setFiles(e.map((f) => f.file as File))}
                  maxFileSize={29}
                />
              </Column>
            ) : (
              <Column span={6} hasNoPadding>
                <div className="ModalFileUpload">
                  <SingleFileUpload
                    ref={fileUploadRef}
                    label="Upload document"
                    onChange={(e) => setFiles([e])}
                    maxFileSize={29}
                  />
                </div>
              </Column>
            )}
          </Row>
          <Row justify="center">
            <Column span={6} hasNoPadding>
              <ActionButtons
                corners="rounded"
                submitButtonLabel="Upload document"
                isLoading={isHandleSubmitLoading}
                onCancel={handleCancel}
                fullWidth
              />
            </Column>
          </Row>
        </form>
      </Modal>
    </div>
  );
};
